import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import FALLBACK_BANNER from "../../../assets/images/banner-image.png";
import HOME_CASINO_IMAGE from "../../../assets/images/home-casino.webp";
import HOME_SPORTS_IMAGE from "../../../assets/images/home-sports.webp";
import { ReactComponent as SportsCategoryIcon } from "../../../assets/images/Frame (10).svg";
import useSiteSettings, {
  resolveSiteBannerItems,
} from "../../../hooks/useSiteSettings";
import { CasinoCardIcon } from "../../Common/Icons/GameCategoryIcons";
import { siteApi } from "../../../lib/api";
import {
  getRows,
} from "../../../utils/gameCatalog";
import { StyledHomeTopShowcase } from "./StyledHomeTopShowcase";
import { isInlineGameActive } from "../../../lib/gameLaunch";

const CATEGORY_ICONS = {
  casino: CasinoCardIcon,
  sports: SportsCategoryIcon,
};

const CategoryIcon = ({ type }) => {
  const Icon = CATEGORY_ICONS[type];
  if (!Icon) return null;

  return (
    <span className={`home-top-category-icon home-top-category-icon-${type}`} aria-hidden="true">
      <Icon {...(type === "sports" ? { "data-ds-icon": "Sports" } : {})} />
    </span>
  );
};

const HomeTopShowcase = () => {
  const { settings, isLoading } = useSiteSettings();
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [summary, setSummary] = useState({
    casinoCount: 0,
    sportsCount: 0,
    isLoading: true,
  });

  useEffect(() => {
    let isMounted = true;

    const loadSummary = async () => {
      try {
        const [casinoResponse, slotsResponse, sportsResponse] = await Promise.all([
          siteApi.getGameVendors({
            game_type: "live-casino",
            order_by_name: "sequence",
            order_by_type: "ASC",
            limit: "ALL",
          }),
          siteApi.getGameVendors({
            game_type: "slots",
            order_by_name: "sequence",
            order_by_type: "ASC",
            limit: "ALL",
          }),
          siteApi.getGameVendors({
            game_type: "live-sports",
            order_by_name: "sequence",
            order_by_type: "ASC",
            limit: "ALL",
          }),
        ]);

        if (!isMounted) return;

        const casinoRows = casinoResponse.success ? getRows(casinoResponse) : [];
        const slotRows = slotsResponse.success ? getRows(slotsResponse) : [];
        const sportsRows = sportsResponse.success ? getRows(sportsResponse) : [];

        setSummary({
          casinoCount: casinoRows.length + slotRows.length,
          sportsCount: sportsRows.length + 4,
          isLoading: false,
        });
      } catch {
        if (isMounted) {
          setSummary((current) => ({ ...current, isLoading: false }));
        }
      }
    };

    loadSummary();

    return () => {
      isMounted = false;
    };
  }, []);

  const backendBannerItems = useMemo(() => resolveSiteBannerItems(settings), [settings]);
  const bannerItems = useMemo(
    () =>
      backendBannerItems.length
        ? backendBannerItems
        : [{ id: "fallback-banner", image: FALLBACK_BANNER, link: "" }],
    [backendBannerItems]
  );
  const activeBanner = bannerItems[activeBannerIndex] || bannerItems[0];

  useEffect(() => {
    setActiveBannerIndex((currentIndex) =>
      Math.min(currentIndex, Math.max(bannerItems.length - 1, 0))
    );
  }, [bannerItems.length]);

  useEffect(() => {
    if (isLoading || bannerItems.length <= 1) return undefined;

    const slideTimer = window.setInterval(() => {
      if (isInlineGameActive()) return;
      setActiveBannerIndex((currentIndex) => (currentIndex + 1) % bannerItems.length);
    }, 4500);

    return () => {
      window.clearInterval(slideTimer);
    };
  }, [bannerItems.length, isLoading]);

  const categoryTiles = useMemo(
    () => [
      {
        title: "카지노",
        subtitle: "카지노 · 슬롯",
        count: summary.casinoCount,
        to: "/casino-games",
        image: HOME_CASINO_IMAGE,
        iconType: "casino",
      },
      {
        title: "스포츠북",
        subtitle: "국내형 · 해외형 · 라이브 · E스포츠",
        count: summary.sportsCount,
        to: "/sports?type=DOMESTIC",
        image: HOME_SPORTS_IMAGE,
        iconType: "sports",
      },
    ],
    [summary]
  );

  return (
    <StyledHomeTopShowcase>
      <div className="home-top-banner">
        {isLoading ? (
          <span className="home-top-banner-skeleton" aria-hidden="true" />
        ) : (
          <>
            {activeBanner?.link ? (
              <a className="home-top-banner-media" href={activeBanner.link}>
                <img
                  key={activeBanner.image}
                  className="home-top-banner-image"
                  src={activeBanner.image}
                  alt="메인 배너"
                />
              </a>
            ) : (
              <div className="home-top-banner-media">
                <img
                  key={activeBanner?.image}
                  className="home-top-banner-image"
                  src={activeBanner?.image}
                  alt="메인 배너"
                />
              </div>
            )}
            {bannerItems.length > 1 ? (
              <div className="home-top-banner-dots" aria-label="메인 배너 선택">
                {bannerItems.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`home-top-banner-dot ${
                      index === activeBannerIndex ? "is-active" : ""
                    }`}
                    aria-label={`${index + 1}번째 배너 보기`}
                    aria-pressed={index === activeBannerIndex}
                    onClick={() => setActiveBannerIndex(index)}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>

      <div className="home-top-category-grid">
        {categoryTiles.map((tile) =>
          summary.isLoading ? (
            <div
              key={tile.title}
              className="home-top-category-card is-loading"
              aria-label={`${tile.title} 불러오는 중`}
            >
              <span className="home-top-category-skeleton" aria-hidden="true" />
            </div>
          ) : (
            <Link key={tile.title} to={tile.to} className="home-top-category-card">
              <img
                className={`home-top-category-image is-${tile.iconType}`}
                src={tile.image}
                alt=""
                aria-hidden="true"
              />
              <span className="home-top-category-footer">
                <span className="home-top-category-title">
                  <CategoryIcon type={tile.iconType} />
                  <span>
                    <strong>{tile.title}</strong>
                    <small>{tile.subtitle}</small>
                  </span>
                </span>
                <span className="home-top-category-count">
                  <span className="live-dot" />
                  {tile.count.toLocaleString("ko-KR")}
                </span>
              </span>
            </Link>
          )
        )}
      </div>
    </StyledHomeTopShowcase>
  );
};

export default HomeTopShowcase;
